import { motion } from "framer-motion";
import {
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Star,
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye,
} from "lucide-react";
import { cn } from "@/utils/cn";
import type { Contact } from "./ContactsPage";

const channelIcons = {
  whatsapp: { icon: MessageCircle, color: "#22C55E", label: "WhatsApp" },
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  widget: { icon: Globe, color: "#2563EB", label: "Chat Widget" },
};

const statusStyles = {
  customer: { bg: "bg-green-50", text: "text-green-600", label: "Cliente" },
  lead: { bg: "bg-amber-50", text: "text-amber-600", label: "Lead" },
  active: { bg: "bg-blue-50", text: "text-blue-600", label: "Ativo" },
  inactive: { bg: "bg-gray-50", text: "text-gray-500", label: "Inativo" },
};

interface ContactTableProps {
  contacts: Contact[];
  onToggleFavorite: (id: string) => void;
  onDelete: (id: string) => void;
  onSelect: (contact: Contact) => void;
  selectedContactId?: string;
}

export function ContactTable({
  contacts,
  onToggleFavorite,
  onDelete,
  onSelect,
  selectedContactId,
}: ContactTableProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-[14px] bg-white shadow-sm border border-[#E2E8F0] overflow-hidden"
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Contato
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Empresa
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Canal
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Conversas
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Último Contato
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-[#64748B]">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {contacts.map((contact, index) => {
            const ChannelIcon = channelIcons[contact.channel].icon;
            const isSelected = selectedContactId === contact.id;

            return (
              <motion.tr
                key={contact.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => onSelect(contact)}
                className={cn(
                  "cursor-pointer transition-colors",
                  isSelected
                    ? "bg-blue-50/50"
                    : "hover:bg-[#F8FAFC]"
                )}
              >
                {/* Contact */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(contact.id);
                      }}
                      className={cn(
                        "transition-colors",
                        contact.isFavorite
                          ? "text-amber-400"
                          : "text-[#E2E8F0] hover:text-amber-400"
                      )}
                    >
                      <Star
                        className="h-4 w-4"
                        fill={contact.isFavorite ? "currentColor" : "none"}
                      />
                    </button>
                    <img
                      src={contact.avatar}
                      alt={contact.name}
                      className="h-9 w-9 rounded-full object-cover"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0F172A] truncate">
                        {contact.name}
                      </p>
                      <p className="text-xs text-[#64748B] truncate">
                        {contact.email}
                      </p>
                    </div>
                  </div>
                </td>

                {/* Company */}
                <td className="px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm text-[#0F172A] truncate">
                      {contact.company}
                    </p>
                    <p className="text-xs text-[#64748B] truncate">
                      {contact.role}
                    </p>
                  </div>
                </td>

                {/* Channel */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="flex h-7 w-7 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${channelIcons[contact.channel].color}15` }}
                    >
                      <ChannelIcon
                        className="h-3.5 w-3.5"
                        style={{ color: channelIcons[contact.channel].color }}
                      />
                    </div>
                    <span className="text-sm text-[#64748B]">
                      {channelIcons[contact.channel].label}
                    </span>
                  </div>
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
                      statusStyles[contact.status].bg,
                      statusStyles[contact.status].text
                    )}
                  >
                    {statusStyles[contact.status].label}
                  </span>
                </td>

                {/* Conversations */}
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-[#0F172A]">
                    {contact.totalConversations}
                  </span>
                </td>

                {/* Last Contact */}
                <td className="px-4 py-3">
                  <span className="text-sm text-[#64748B]">
                    {contact.lastContact}
                  </span>
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(contact);
                      }}
                      className="rounded-lg p-1.5 text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB]"
                      title="Ver detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-lg p-1.5 text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB]"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(contact.id);
                      }}
                      className="rounded-lg p-1.5 text-[#64748B] hover:bg-red-50 hover:text-[#EF4444]"
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-lg p-1.5 text-[#64748B] hover:bg-[#F1F5F9]"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </motion.div>
  );
}